import {Trans} from '@lingui/macro';
import {find, isString} from 'lodash';
import React, {Children} from 'react';
import styled from 'styled-components';

import {escapeRegex, isBasicLink} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {NodeTransform} from '../../../../../srcCommon/formatters/markdown/markdownFormatterTypes';
import {
  CommentUnfurlErrorsEnum,
  CommentUnfurlModel,
  CommentUnfurlTypesEnum,
} from '../../../../models/api/commentUnfurlModel';

/*
 * Style.
 */

const StyledUnfurlSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

/*
 * Transform.
 */

export function makeMarkdownTransformUnfurls(unfurls: ReadonlyArray<CommentUnfurlModel>): NodeTransform {
  if (unfurls.length === 0) {
    return (node) => node;
  }

  const unfurlsRegexParts = unfurls.map((unfurl) => escapeRegex(unfurl.token)).join('|');
  const unfurlsRegex = new RegExp(`(${unfurlsRegexParts})`);
  return (node) =>
    Children.map(node, (child) => {
      // We only process strings.
      if (!isString(child)) {
        return child;
      }

      // Split the string by all unfurl tokens in this comment.
      return child.split(unfurlsRegex).map((part) => {
        const unfurl = find(unfurls, {token: part});
        if (!unfurl) {
          return part;
        }

        // Only apply style/tooltip to id unfurls, not link unfurls
        if (isBasicLink(part)) {
          return part;
        }

        const renderedUnfurl = <StyledUnfurlSpan>{part}</StyledUnfurlSpan>;
        if (unfurl.error) {
          return (
            <TooltipCoordinator isInline render={() => renderTooltip(unfurl)}>
              {renderedUnfurl}
            </TooltipCoordinator>
          );
        }

        return renderedUnfurl;
      });
    });
}

function renderTooltip(unfurl: CommentUnfurlModel) {
  if (unfurl.error === CommentUnfurlErrorsEnum.FORBIDDEN) {
    return <Trans id="comment-unfurl-tooltip-forbidden">You need to be invited to view this</Trans>;
  }

  return unfurl.type === CommentUnfurlTypesEnum.CONVERSATION ? (
    <Trans id="comment-unfurl-tooltip-conversation-not-found">This conversation could not be found</Trans>
  ) : (
    <Trans id="comment-unfurl-tooltip-message-not-found">This message could not be found</Trans>
  );
}
